import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { addMinutes, format, parse } from 'date-fns'
import { yupResolver } from '@hookform/resolvers/yup'

import { CustomerAutocomplete } from '../../../components/forms/fields/CustomerAutocomplete'
import { SelectController } from '../../../components/forms/fields/SelectController'
import { TextFieldController } from '../../../components/forms/fields/TextFieldController'
import { SkierFieldArray } from '../../../components/forms/SkierFieldArray'
import { toast } from '../../../components/LsToast'
import { CURRENCY } from '../../../constants/currency'
import { useEscapeKey } from '../../../hooks/useEscapeKey'
import { Path } from '../../../router/BaseRouter/enums'
import { createLesson, deleteLesson, updateLesson } from '../../../service/lessons.service'
import {
  AvailabilityMap,
  BookingEntryDto,
  EMPTY_SKIER,
  InstructorDto,
  LessonDto,
  LessonStatus,
  LessonType,
  ProductDto,
  Skier,
} from '../../../service/types'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'
import { END_TIME_SLOTS, START_TIME_SLOTS } from '../constants'
import { CheckoutDialog } from './CheckoutDialog'
import { CustomerOrderSection } from './CustomerOrderSection'
import { filterAvailableInstructors } from './filterAvailableInstructors'
import { LessonFormValues, useSchema } from './schema'
import { useLessonPricing } from './useLessonPricing'

export type LessonPreview = {
  startTime: string
  endTime: string
  instructorIds: string[]
} | null

type Props = {
  open: boolean
  onClose: () => void
  onSaved: () => void
  lesson?: LessonDto | null
  defaultInstructorId?: string
  defaultTime?: string
  defaultDate: string
  defaultCustomerId?: string
  defaultSkiers?: Skier[]
  requestId?: string
  requestSummary?: string
  instructors: InstructorDto[]
  products: ProductDto[]
  availabilityMap: AvailabilityMap
  onPreviewChange?: (preview: LessonPreview) => void
}

type RequestSummaryData = {
  customerName?: string
  lessonType?: string
  discipline?: string
  level?: string
  skiers?: { name: string; age: number }[]
  notes?: string
}

const EMPTY_BOOKING_ENTRY = { customerId: '', clientName: '', skiers: [EMPTY_SKIER] }
export const DRAWER_WIDTH = { sm: 420, xl: 540 }

export const LessonDialog: FC<Props> = ({
  open,
  onClose,
  onSaved,
  lesson,
  defaultInstructorId,
  defaultTime,
  defaultDate,
  defaultCustomerId,
  defaultSkiers,
  requestId,
  requestSummary,
  instructors,
  products,
  availabilityMap,
  onPreviewChange,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const schema = useSchema()
  const [lessonType, setLessonType] = useState<LessonType>(LessonType.Private)
  const isCompleted = lesson?.status === LessonStatus.Completed
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const skipEndTimeAutoUpdate = useRef(false)
  const prevStartTimeRef = useRef<string>('')

  useEscapeKey(onClose, open)

  const activeProducts = useMemo(
    () => products.filter((p) => p.isActive && p.lessonType === lessonType),
    [products, lessonType],
  )

  const { handleSubmit, control, reset, watch, setValue, getValues } = useForm<LessonFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      date: defaultDate,
      startTime: '',
      endTime: '',
      instructorIds: [],
      productId: '',
      customerId: '',
      clientName: '',
      skiers: [EMPTY_SKIER],
      bookings: [],
      notes: '',
    },
  })

  // Group lesson bookings field array
  const {
    fields: bookingFields,
    append: appendBooking,
    remove: removeBooking,
  } = useFieldArray({
    control,
    name: 'bookings',
  })

  const productId = watch('productId')
  const startTimeValue = watch('startTime')
  const endTimeValue = watch('endTime')
  const skiersValue = watch('skiers')
  const bookingsValue = watch('bookings')
  const customerIdValue = watch('customerId')

  const isGroup = lessonType === LessonType.Group

  const availableInstructors = useMemo(
    () => filterAvailableInstructors(instructors, availabilityMap, startTimeValue, endTimeValue),
    [instructors, availabilityMap, startTimeValue, endTimeValue],
  )

  const instructorIdsValue = watch('instructorIds')
  const unavailableAssigned = useMemo(() => {
    const assignedIds = instructorIdsValue ?? []
    const availableIds = new Set(availableInstructors.map((i) => i.id))
    return instructors.filter((i) => assignedIds.includes(i.id) && !availableIds.has(i.id))
  }, [instructorIdsValue, availableInstructors, instructors])

  const parsedRequestSummary: RequestSummaryData | null = useMemo(() => {
    if (!requestSummary) return null
    try {
      return JSON.parse(requestSummary) as RequestSummaryData
    } catch {
      return null
    }
  }, [requestSummary])

  const pricing = useLessonPricing({
    products,
    productId,
    lessonType,
    skiers: skiersValue ?? [],
    bookings: bookingsValue ?? [],
  })

  useEffect(() => {
    if (lesson) {
      // Editing existing lesson — skip endTime auto-update to preserve the lesson's actual endTime
      skipEndTimeAutoUpdate.current = true
      setLessonType(lesson.lessonType ?? LessonType.Private)
      // Re-group flat booking records by customer
      const customerMap = new Map<string, { customerId: string; clientName: string; skiers: Skier[] }>()
      for (const b of lesson.bookings ?? []) {
        const existing = customerMap.get(b.customerId)
        if (existing) {
          existing.skiers.push({ name: b.skierName, age: b.skierAge })
        } else {
          customerMap.set(b.customerId, {
            customerId: b.customerId,
            clientName: b.customer?.name ?? '',
            skiers: [{ name: b.skierName, age: b.skierAge }],
          })
        }
      }
      const bookingEntries =
        customerMap.size > 0 ? [...customerMap.values()] : [{ customerId: '', clientName: '', skiers: [EMPTY_SKIER] }]

      reset({
        date: lesson.date,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
        instructorIds: lesson.instructorIds ?? [],
        productId: lesson.productId ?? '',
        customerId: lesson.lessonType === LessonType.Group ? '' : (bookingEntries[0]?.customerId ?? ''),
        clientName: lesson.lessonType === LessonType.Group ? '' : (bookingEntries[0]?.clientName ?? ''),
        skiers:
          lesson.lessonType === LessonType.Group
            ? []
            : bookingEntries[0]?.skiers?.length > 0
              ? bookingEntries[0].skiers
              : [EMPTY_SKIER],
        bookings:
          lesson.lessonType === LessonType.Group
            ? bookingEntries.length > 0
              ? bookingEntries
              : [EMPTY_BOOKING_ENTRY]
            : [],
        notes: lesson.notes ?? '',
      })
    } else {
      // Creating new lesson
      skipEndTimeAutoUpdate.current = true
      const requestType = parsedRequestSummary?.lessonType === 'group' ? LessonType.Group : LessonType.Private
      setLessonType(requestType)
      const startTime = defaultTime ?? ''
      const maxEndTime = END_TIME_SLOTS[END_TIME_SLOTS.length - 1]
      const rawEndTime = defaultTime ? format(addMinutes(parse(defaultTime, 'HH:mm', new Date()), 60), 'HH:mm') : ''
      const endTime = rawEndTime > maxEndTime ? maxEndTime : rawEndTime

      reset({
        date: defaultDate,
        startTime,
        endTime,
        instructorIds: defaultInstructorId ? [defaultInstructorId] : [],
        productId: '',
        customerId: defaultCustomerId ?? '',
        clientName: '',
        skiers: defaultSkiers?.length ? defaultSkiers : [EMPTY_SKIER],
        bookings: [],
        notes: '',
      })
    }
  }, [
    lesson,
    open,
    defaultInstructorId,
    defaultTime,
    defaultDate,
    defaultCustomerId,
    defaultSkiers,
    parsedRequestSummary,
    reset,
  ])

  // When startTime changes, shift endTime to preserve the current lesson duration.
  // When product changes, auto-set endTime from product duration.
  useEffect(() => {
    const prevStartTime = prevStartTimeRef.current
    const startChanged = startTimeValue !== prevStartTime
    prevStartTimeRef.current = startTimeValue

    // Skip during form reset for existing lessons (preserves the lesson's actual endTime)
    if (skipEndTimeAutoUpdate.current) {
      skipEndTimeAutoUpdate.current = false
      return
    }

    if (!startTimeValue) return

    if (startChanged && prevStartTime) {
      // Start time changed → shift endTime to preserve the current duration
      const currentEndTime = getValues('endTime')
      if (currentEndTime) {
        const oldStart = parse(prevStartTime, 'HH:mm', new Date())
        const oldEnd = parse(currentEndTime, 'HH:mm', new Date())
        const durationMinutes = (oldEnd.getTime() - oldStart.getTime()) / 60000
        if (durationMinutes > 0) {
          const newStart = parse(startTimeValue, 'HH:mm', new Date())
          const newEnd = addMinutes(newStart, durationMinutes)
          const maxEndTime = END_TIME_SLOTS[END_TIME_SLOTS.length - 1]
          const computed = format(newEnd, 'HH:mm')
          setValue('endTime', computed > maxEndTime ? maxEndTime : computed)
          return
        }
      }
    }

    // Product selected → set from product duration
    if (!productId) return
    const product = activeProducts.find((p) => p.id === productId)
    if (!product) return
    const start = parse(startTimeValue, 'HH:mm', new Date())
    const end = addMinutes(start, product.durationMinutes)
    const maxEndTime = END_TIME_SLOTS[END_TIME_SLOTS.length - 1]
    const computed = format(end, 'HH:mm')
    setValue('endTime', computed > maxEndTime ? maxEndTime : computed)
  }, [productId, startTimeValue, activeProducts, setValue, getValues])

  // Emit preview state for grid visualization (new lessons only)
  useEffect(() => {
    if (!onPreviewChange) return
    if (!open || lesson) {
      onPreviewChange(null)
      return
    }
    if (startTimeValue && endTimeValue) {
      onPreviewChange({
        startTime: startTimeValue,
        endTime: endTimeValue,
        instructorIds: instructorIdsValue ?? [],
      })
    } else {
      onPreviewChange(null)
    }
  }, [open, lesson, startTimeValue, endTimeValue, instructorIdsValue, onPreviewChange])

  // When switching from private→group, seed bookings and clear private skiers
  useEffect(() => {
    if (isGroup && bookingFields.length === 0) {
      appendBooking(EMPTY_BOOKING_ENTRY)
    }
    if (isGroup) {
      setValue('skiers', [])
    }
  }, [isGroup, bookingFields.length, appendBooking, setValue])

  const handleLessonTypeChange = (_: unknown, value: LessonType | null) => {
    if (!value) return
    setLessonType(value)
    setValue('productId', '')
    if (value === LessonType.Group) {
      setValue('customerId', '')
      setValue('clientName', '')
      setValue('skiers', [])
    } else {
      setValue('bookings', [])
      setValue('skiers', [EMPTY_SKIER])
    }
  }

  const onSubmit = async (data: LessonFormValues) => {
    try {
      const instructorIds = (data.instructorIds ?? []).filter(Boolean) as string[]

      let bookings: BookingEntryDto[]
      if (isGroup) {
        bookings = (data.bookings ?? []).map((b) => ({
          customerId: b.customerId!,
          skiers: (b.skiers ?? []).map((s) => ({ name: s.name!, age: s.age! })),
        }))
      } else {
        bookings = [
          {
            customerId: data.customerId!,
            skiers: (data.skiers ?? []).map((s) => ({ name: s.name!, age: s.age! })),
          },
        ]
      }

      const base = {
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        productId: data.productId,
        bookings,
        notes: data.notes || undefined,
      }

      if (lesson) {
        await updateLesson(lesson.id, {
          ...base,
          instructorIds,
        })
      } else {
        await createLesson({
          ...base,
          instructorIds: instructorIds.length > 0 ? instructorIds : undefined,
          requestId: requestId || undefined,
        })
      }

      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const handleDelete = async () => {
    if (lesson) {
      try {
        await deleteLesson(lesson.id)
        toast.success(t('general.notification.deletedSuccessfully'))
        onSaved()
        onClose()
      } catch {
        toast.error(t('general.notification.unexpectedError'))
      }
    }
  }

  const handleViewCustomer = (customerId: string) => {
    onClose()
    navigate(`/customer/${customerId}`)
  }

  const handleViewRequest = () => {
    if (lesson?.requestId) {
      onClose()
      navigate(`${Path.REQUESTS}?requestId=${lesson.requestId}`)
    }
  }

  const handleViewReceipt = (receiptId: string) => {
    onClose()
    navigate(`${Path.RECEIPTS}/${receiptId}`)
  }

  // Extract unique receipts from paid bookings (grouped by receiptId)
  const receiptLinks = useMemo(() => {
    if (!lesson) return []
    const receiptMap = new Map<string, { receiptId: string; receiptNumber: number; customerName: string }>()
    for (const b of lesson.bookings) {
      if (b.receiptId && b.receiptNumber && !receiptMap.has(b.receiptId)) {
        receiptMap.set(b.receiptId, {
          receiptId: b.receiptId,
          receiptNumber: b.receiptNumber,
          customerName: b.customer?.name ?? '',
        })
      }
    }
    return [...receiptMap.values()]
  }, [lesson])

  const handleMarkCompleted = async () => {
    if (!lesson) return
    try {
      await updateLesson(lesson.id, { status: LessonStatus.Completed })
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const handleCancelLesson = async () => {
    if (!lesson) return
    try {
      await updateLesson(lesson.id, { status: LessonStatus.Cancelled })
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const handleReschedule = async () => {
    if (!lesson) return
    try {
      await updateLesson(lesson.id, { status: LessonStatus.Scheduled })
      toast.success(t('general.notification.savedSuccessfully'))
      onSaved()
      onClose()
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err) || t('general.notification.unexpectedError'))
    }
  }

  const formContent = (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{lesson ? t('lessons.editLesson') : t('lessons.addLesson')}</Typography>
          {lesson && (
            <Chip
              label={
                lesson.status === LessonStatus.Scheduled && lesson.paymentStatus === 'paid'
                  ? t('lessonStatus.paid')
                  : t(`lessonStatus.${lesson.status}`)
              }
              color={
                lesson.status === LessonStatus.Cancelled
                  ? 'error'
                  : lesson.status === LessonStatus.Completed
                    ? 'primary'
                    : lesson.paymentStatus === 'paid'
                      ? 'success'
                      : 'info'
              }
              size="small"
            />
          )}
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      {lesson && lesson.status === LessonStatus.Scheduled && (
        <Box sx={{ display: 'flex', gap: 1, px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          {lesson.paymentStatus === 'paid' && (
            <Button variant="contained" color="primary" size="large" onClick={handleMarkCompleted}>
              {t('lessons.form.markCompleted')}
            </Button>
          )}
          {lesson.paymentStatus !== 'paid' && (
            <Button variant="contained" color="primary" size="large" onClick={() => setCheckoutOpen(true)}>
              {t('lessons.form.checkout')}
            </Button>
          )}
          <Button variant="outlined" color="error" size="large" onClick={handleCancelLesson}>
            {t('lessons.form.cancelLesson')}
          </Button>
        </Box>
      )}
      {receiptLinks.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 3, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          {receiptLinks.map((r) => (
            <Button
              key={r.receiptId}
              size="small"
              variant="text"
              startIcon={<ReceiptLongIcon />}
              onClick={() => handleViewReceipt(r.receiptId)}
            >
              {receiptLinks.length > 1
                ? `${r.customerName} — ${t('lessons.viewReceipt', { number: r.receiptNumber })}`
                : t('lessons.viewReceipt', { number: r.receiptNumber })}
            </Button>
          ))}
        </Box>
      )}
      {lesson && (lesson.status === LessonStatus.Cancelled || lesson.status === LessonStatus.Completed) && (
        <Box sx={{ display: 'flex', gap: 1, px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Button variant="contained" color="primary" size="large" onClick={handleReschedule}>
            {t('lessons.form.reschedule')}
          </Button>
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <ToggleButtonGroup
            value={lessonType}
            exclusive
            onChange={handleLessonTypeChange}
            fullWidth
            size="small"
            disabled={!!lesson}
          >
            <ToggleButton value={LessonType.Private}>{t('lessonType.private')}</ToggleButton>
            <ToggleButton value={LessonType.Group}>{t('lessonType.group')}</ToggleButton>
          </ToggleButtonGroup>

          {lesson?.requestId && (
            <Button size="small" variant="text" onClick={handleViewRequest} sx={{ alignSelf: 'flex-start' }}>
              {t('lessons.viewRequest')}
            </Button>
          )}
          {parsedRequestSummary && (
            <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('lessons.fromRequest')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
                <Chip size="small" label={t(`lessonType.${parsedRequestSummary.lessonType}`)} />
                <Chip size="small" label={t(`discipline.${parsedRequestSummary.discipline}`)} />
                <Chip size="small" label={t(`skierLevel.${parsedRequestSummary.level}`)} />
              </Box>
              {parsedRequestSummary.skiers && parsedRequestSummary.skiers.length > 0 && (
                <Typography variant="body2">
                  {t('requests.form.skiers')}:{' '}
                  {parsedRequestSummary.skiers.map((s) => `${s.name} (${s.age})`).join(', ')}
                </Typography>
              )}
              {parsedRequestSummary.notes && (
                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  {parsedRequestSummary.notes}
                </Typography>
              )}
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <Box flex={1}>
              <Typography variant="caption">{t('lessons.form.startTime')}</Typography>
              <SelectController
                name="startTime"
                control={control}
                disabled={isCompleted}
                options={START_TIME_SLOTS.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              />
            </Box>
            <Box flex={1}>
              <Typography variant="caption">{t('lessons.form.endTime')}</Typography>
              <SelectController
                name="endTime"
                control={control}
                disabled={isCompleted}
                options={END_TIME_SLOTS.map((time) => (
                  <MenuItem key={time} value={time}>
                    {time}
                  </MenuItem>
                ))}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="caption">{t('lessons.form.instructor')}</Typography>
            <Controller
              name="instructorIds"
              control={control}
              render={({ field }) => (
                <FormControl
                  variant="filled"
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': { paddingTop: '9px', paddingBottom: '8px' },
                  }}
                >
                  <Select
                    {...field}
                    disabled={isCompleted}
                    multiple={isGroup}
                    value={isGroup ? (field.value ?? []) : ((field.value as string[])?.[0] ?? '')}
                    onChange={(e) => {
                      if (isGroup) {
                        field.onChange(e.target.value)
                      } else {
                        const val = e.target.value as string
                        field.onChange(val ? [val] : [])
                      }
                    }}
                    renderValue={(selected) => {
                      if (isGroup) {
                        const ids = selected as string[]
                        return ids.map((id) => instructors.find((i) => i.id === id)?.name ?? id).join(', ')
                      }
                      const id = selected as string
                      if (!id) return t('dayPlan.unassigned')
                      return instructors.find((i) => i.id === id)?.name ?? id
                    }}
                  >
                    {!isGroup && (
                      <MenuItem value="">
                        <em>{t('dayPlan.unassigned')}</em>
                      </MenuItem>
                    )}
                    {availableInstructors.map((inst) => (
                      <MenuItem key={inst.id} value={inst.id}>
                        {isGroup && <Checkbox checked={((field.value as string[]) ?? []).includes(inst.id)} />}
                        <ListItemText primary={inst.name} />
                      </MenuItem>
                    ))}
                    {unavailableAssigned.map((inst) => (
                      <MenuItem key={inst.id} value={inst.id} sx={{ opacity: 0.5 }}>
                        {isGroup && <Checkbox checked={((field.value as string[]) ?? []).includes(inst.id)} />}
                        <ListItemText primary={`${inst.name} (${t('dayPlan.instructorUnavailable')})`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Box>
            <Typography variant="caption">{t('lessons.form.product')}</Typography>
            <SelectController
              name="productId"
              control={control}
              disabled={isCompleted}
              options={activeProducts.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name} ({product.durationMinutes} min, {product.price} {CURRENCY})
                </MenuItem>
              ))}
            />
          </Box>

          {/* ─── Private lesson: single customer + skiers ─── */}
          {!isGroup && (
            <>
              <Box>
                <Typography variant="caption">{t('lessons.form.customer')}</Typography>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <Box flex={1}>
                    <CustomerAutocomplete
                      control={control}
                      setValue={setValue}
                      customerIdField="customerId"
                      disabled={isCompleted}
                    />
                  </Box>
                  {lesson && customerIdValue && (
                    <IconButton size="small" onClick={() => handleViewCustomer(customerIdValue)} sx={{ mt: 0.25 }}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {t('lessons.form.skiers')}
                </Typography>
                <SkierFieldArray control={control} name="skiers" prices={pricing?.skierPrices} disabled={isCompleted} />
              </Box>
            </>
          )}

          {/* ─── Group lesson: multiple customers with own skiers ─── */}
          {isGroup && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                {t('lessons.form.customers')}
              </Typography>
              {bookingFields.map((field, index) => {
                let entryPrices: number[] | undefined
                if (pricing) {
                  const offset = (bookingsValue ?? [])
                    .slice(0, index)
                    .reduce((sum, b) => sum + (b.skiers?.length ?? 0), 0)
                  const count = bookingsValue?.[index]?.skiers?.length ?? 0
                  entryPrices = pricing.skierPrices.slice(offset, offset + count)
                }

                // Derive per-customer payment status from lesson bookings
                let customerPaymentStatus: 'paid' | 'partially_paid' | null = null
                if (lesson) {
                  const custId = bookingsValue?.[index]?.customerId
                  if (custId) {
                    const custBookings = lesson.bookings.filter((b) => b.customerId === custId)
                    if (custBookings.length > 0) {
                      const allPaid = custBookings.every((b) => b.receiptId)
                      const somePaid = custBookings.some((b) => b.receiptId)
                      if (allPaid) customerPaymentStatus = 'paid'
                      else if (somePaid) customerPaymentStatus = 'partially_paid'
                    }
                  }
                }

                return (
                  <CustomerOrderSection
                    key={field.id}
                    index={index}
                    control={control}
                    setValue={setValue}
                    onRemove={() => removeBooking(index)}
                    canRemove={bookingFields.length > 1}
                    prices={entryPrices}
                    paymentStatus={customerPaymentStatus}
                    onViewCustomer={lesson ? handleViewCustomer : undefined}
                    disabled={isCompleted}
                  />
                )
              })}
              {!isCompleted && (
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={() => appendBooking(EMPTY_BOOKING_ENTRY)}
                  sx={{ mt: 0.5 }}
                >
                  {t('lessons.form.addCustomer')}
                </Button>
              )}
            </Box>
          )}

          <Box>
            <Typography variant="caption">{t('lessons.form.notes')}</Typography>
            <TextFieldController name="notes" control={control} multiline disabled={isCompleted} />
          </Box>
        </Box>
      </Box>
      {pricing && (
        <Box sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'right' }}>
            {t('lessons.form.totalPrice')}: {pricing.total} {CURRENCY}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        {lesson && !isCompleted && (
          <Button onClick={handleDelete} color="error" sx={{ mr: 'auto' }}>
            {t('general.button.delete')}
          </Button>
        )}
        <Box sx={{ ml: lesson && !isCompleted ? 0 : 'auto' }} />
        <Button onClick={onClose}>{t('general.button.cancel')}</Button>
        {!isCompleted && (
          <Button type="submit" variant="contained">
            {t('general.button.save')}
          </Button>
        )}
      </Box>
    </form>
  )

  const checkoutDialog = lesson ? (
    <CheckoutDialog
      open={checkoutOpen}
      onClose={() => setCheckoutOpen(false)}
      onPaid={() => {
        onSaved()
      }}
      bookings={lesson.bookings}
    />
  ) : null

  if (isSmallScreen) {
    return (
      <>
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: '100%' } }}>
          {formContent}
        </Drawer>
        {checkoutDialog}
      </>
    )
  }

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderLeft: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {formContent}
      </Drawer>
      {checkoutDialog}
    </>
  )
}
